import { useCallback, useMemo } from 'react';
import Raven from 'raven-js';
import { useGetPublicProductTierOptionsV2Query } from 'pricing-pages-ui-public-app/__generated__/chirp/com/hubspot/buying/rpc/PublicProductRpc.hooks';
import { DEFAULT_CHIRP_TIMEOUT } from '../../constants/chirpDefaults';
import { isCountryCodeString } from '../../utils/currencyTypes';
import { getBrowserLocale, getCountryCode } from '../../utils/getPreferredLanguage';
/*
 * Single owner of the public HSOC tier-options query. Both the merch cards
 * (useHsocPublicPricing) and the feature table read tier data from this one
 * hook, so the query + its variables + locale are defined and fetched once (the
 * public early requester warms the same variables, and there is no risk of the
 * two surfaces drifting on request variables). Mirrors the in-app
 * useHsocInAppTierOptions.
 */
export function useHsocPublicTierOptions(currencyCode, options = {}) {
  const {
    skip = false
  } = options;
  const browserLocale = useMemo(() => getBrowserLocale(), []);
  const rawCountryCode = useMemo(() => getCountryCode(), []);
  const preferredCountryCode = useMemo(() => isCountryCodeString(rawCountryCode) ? rawCountryCode : undefined, [rawCountryCode]);
  const handleError = useCallback(error => {
    Raven.captureException(error, {
      fingerprint: ['failed-to-fetch-public-product-tier-options-v2'],
      extra: {
        currencyCode,
        preferredCountryCode,
        browserLocale
      }
    });
  }, [currencyCode, preferredCountryCode, browserLocale]);

  /* Skip until the caller's currency has resolved from its real server/URL
   * value (see useCurrency().isLoading). Firing earlier means currencyCode is
   * still CurrencyContext's serverDefault fallback ('USD'), which almost never
   * matches the browser-locale-guessed currency the public early requester
   * prefetched (publicEarlyRequester.ts) for non-US locales — turning what
   * should be an instant cache hit into a real, un-prewarmed network request. */
  const {
    data,
    loading,
    error
  } = useGetPublicProductTierOptionsV2Query({
    variables: {
      browserLocale,
      preferredCountryCode,
      currencyCode
    },
    context: {
      timeout: DEFAULT_CHIRP_TIMEOUT
    },
    onError: handleError,
    skip
  });
  return {
    productsByTier: data === null || data === void 0 ? void 0 : data.productsByTier,
    products: data === null || data === void 0 ? void 0 : data.products,
    loading: loading || skip,
    error,
    browserLocale
  };
}